import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Button } from "@mui/material";
import { $getNodeByKey } from "lexical";
import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { getAspectRatio } from "../../../../../../utils/aspect-ratio";
import { useClaims } from "../../../../../generic/hooks/use-claims";
import { $createDesignHuddleNodeFromAsset } from "../../../nodes/design-huddle-mui";
import { DefaultProportionOfMaxWidth } from "../../../nodes/design-huddle-node";
import { INSERT_IMAGE_COMMAND } from "../../../types/commands";
import { $insertNode } from "../../../utils/insert-node";
import { insertUrl } from "../../image-plugin";

import type { CreateAssetResult } from "../../../../../assets/file-upload";
import type { Asset } from "../../../../../assets/server/digital-asset-service";
import type { FC } from "react";

export const InsertImageButton: FC<{
  buttonText: string;
  asset: Pick<Asset, "content_type" | "public_url" | "mime_type" | "project_id" | "page_id"> | null;
  onClose: () => void;
  onInsert?: (url: string) => Promise<CreateAssetResult>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  nodeKey?: string;
  disabled?: boolean;
}> = ({ buttonText, asset, loading, nodeKey, setLoading, onClose, onInsert, disabled = false }) => {
  const [editor] = useLexicalComposerContext();
  const user = useClaims();

  const handleInsertAsset = useCallback(async () => {
    setLoading(true);
    if (!asset || !user) return;

    if (
      asset.content_type === "Uploaded File" &&
      asset.public_url &&
      asset.mime_type?.startsWith("image/")
    ) {
      try {
        const file = await insertUrl(asset.public_url, "work_email");
        const result = onInsert && (await onInsert(file.url));
        setLoading(false);

        if (result?.success) {
          onClose();
          editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
            ...file,
            sourceAssetId: result.id,
          });
        }
        return;
      } catch (error) {
        console.error(error);
        toast.error("Failed to insert image");
        setLoading(false);
        return;
      }
    }

    const aspect = asset.public_url ? await getAspectRatio(asset.public_url) : undefined;
    const cbResult = onInsert && (await onInsert(asset.content_type));
    editor.update(
      () => {
        if (asset.public_url && asset.project_id) {
          const node = $createDesignHuddleNodeFromAsset(
            editor._headless,
            user["http://changeengine.com/account_id"],
            asset.project_id,
            DefaultProportionOfMaxWidth,
            aspect,
            asset.page_id ?? undefined,
            asset.public_url,
            cbResult?.success ? cbResult.id : undefined,
            true,
          );

          if (nodeKey) {
            const oldNode = $getNodeByKey(nodeKey);
            oldNode && oldNode.replace(node);
          } else {
            $insertNode(node);
          }
        }
      },
      { discrete: true },
    );
    setLoading(false);
    onClose();
  }, [editor, asset, user, nodeKey, onClose, onInsert, setLoading]);

  return (
    (asset?.mime_type?.startsWith("image/") || asset?.content_type !== "Uploaded File") && (
      <Button
        data-analytics-id="editor-content-library-insert-image"
        data-testid="insert-upload-as-image-button"
        type="button"
        disabled={disabled || asset === null}
        loading={loading}
        onClick={() => {
          void handleInsertAsset();
        }}
      >
        {buttonText}
      </Button>
    )
  );
};
